import { useQueries } from '@tanstack/react-query';
import { factoryGetProfessionalProfileKey } from '../factories/queries/factoryGetProfessionalProfileKey';
import { ProfessionalsService } from '../services/ProfessionalsService';
import { IProfessionalProfile } from '../types/IProfessionalProfile';

const FIVE_MINUTES = 5 * 60 * 1000;

interface IUseGetProfessionalProfilesOptions {
  officeId?: string;
  jobProfession?: string;
  enabled?: boolean;
}

interface IUseGetProfessionalProfilesResult {
  profiles: IProfessionalProfile[];
  isLoading: boolean;
}

/**
 * Module-level so the observer can skip recombining on every render: it
 * compares the function by identity, and only falls back to a deep-equality
 * check on the result when that identity changed.
 */
const combineProfiles = (
  results: { data?: IProfessionalProfile; isLoading: boolean }[],
): IUseGetProfessionalProfilesResult => ({
  profiles: results.flatMap((result) => (result.data ? [result.data] : [])),
  isLoading: results.some((result) => result.isLoading),
});

/**
 * Loads several professionals' profiles at once, one request each.
 *
 * There is no bulk endpoint — `/professionals/:id/profile` is per professional —
 * so this is a `useQueries` fan-out rather than a single fetch. Callers are
 * expected to pass only the ids they could not get from a list endpoint, which
 * in practice is a handful at most.
 *
 * A profile that fails to load is simply absent from `profiles`: these fill
 * gaps in a list that already renders, so one bad id must not blank the rest.
 */
export const useGetProfessionalProfiles = (
  professionalIds: string[],
  { officeId, jobProfession, enabled = true }: IUseGetProfessionalProfilesOptions = {},
): IUseGetProfessionalProfilesResult =>
  useQueries({
    queries: professionalIds.map((professionalId) => ({
      queryKey: factoryGetProfessionalProfileKey({ professionalId, officeId, jobProfession }),
      queryFn: () =>
        ProfessionalsService.getProfessionalProfile({ professionalId, officeId, jobProfession }),
      enabled: enabled && !!professionalId,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: FIVE_MINUTES,
    })),
    combine: combineProfiles,
  });
